import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  MobileStepper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import HomeIcon from "@mui/icons-material/Home";
import MapIcon from "@mui/icons-material/Map";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import RouteIcon from "@mui/icons-material/Route";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

const STORAGE_KEY = "flow-whats-new-seen";

interface Feature {
  version: string;
  label: string;
  icon: React.ReactElement;
  description: string;
  /** Path relative to public folder, e.g. "/whats-new/order-management.png" */
  image?: string;
  /** Hex color for the icon hero area when no image is provided */
  color?: string;
}

// Each feature has a unique version. Only unseen features are shown.
// To add a new feature: append to this array with a new version string.
// To include a screenshot: add an image to public/whats-new/ and set the image path.
const allFeatures: Feature[] = [
  {
    version: "2026-04-a",
    label: "Welcome to Flow",
    icon: <HomeIcon sx={{ fontSize: 64, color: "#fff" }} />,
    color: "#1a73e8",
    description:
      "Flow is the new home for trip management, replacing the legacy Dashboard. Everything you need — trips, vehicles, employees, and more — is now in one modern interface.",
    image: "/whats-new/home.jpg",
  },
  {
    version: "2026-04-b",
    label: "Order Management",
    icon: <FactCheckIcon sx={{ fontSize: 64, color: "#fff" }} />,
    color: "#2e7d32",
    description:
      "The Order Management page gives dispatchers a real-time queue of incoming trip requests with filtering by stage, client, hub, and date range. Click any trip to open it directly.",
    image: "/whats-new/order-management.jpg",
  },
  {
    version: "2026-04-c",
    label: "Trip Details",
    icon: <RouteIcon sx={{ fontSize: 64, color: "#fff" }} />,
    color: "#1565c0",
    description:
      "Each trip has a detailed view showing client info, pickup/dropoff points, driver assignment, dispatcher notes, and order logs. Use the Trip, Map, and Closer tabs to navigate between views.",
    image: "/whats-new/trip-details.jpg",
  },
  {
    version: "2026-04-d",
    label: "Trip Actions",
    icon: <MoreVertIcon sx={{ fontSize: 64, color: "#fff" }} />,
    color: "#37474f",
    description:
      "The Actions menu on each trip lets you Edit Header details, Accept or Decline a trip, Cancel it, Clone it to create a copy, Split it into multiple trips, or run Validate to check for errors.",
    image: "/whats-new/trip-detail-actions.jpg",
  },
  {
    version: "2026-04-e",
    label: "Trip Closer & Events",
    icon: <RouteIcon sx={{ fontSize: 64, color: "#fff" }} />,
    color: "#e65100",
    description:
      "The Closer tab shows the full event timeline for a trip. Yellow rows are suggested events — these are automatically generated from GPS data or added when required events are missing. Uncheck the Active column to dismiss a suggestion. Validation errors at the top highlight what needs to be corrected before the trip can be approved.",
    image: "/whats-new/trip-closer.jpg",
  },
  {
    version: "2026-04-f",
    label: "Trip Map & GPS",
    icon: <GpsFixedIcon sx={{ fontSize: 64, color: "#fff" }} />,
    color: "#6a1b9a",
    description:
      "The Map tab shows the vehicle's GPS route overlaid with trip events and stops. Flow automatically detects GPS stops and suggests events — stops near pickup or dropoff points are promoted to required events, saving manual entry.",
    image: "/whats-new/trip-map.jpg",
  },
  {
    version: "2026-04-g",
    label: "Help Guide",
    icon: <HelpOutlineIcon sx={{ fontSize: 64, color: "#fff" }} />,
    color: "#455a64",
    description:
      "Need a refresher? Click the help icon in the top navigation bar to open the full help guide in a new window. It covers all the features shown in this tour with screenshots and descriptions.",
    image: "/whats-new/help.jpg",
  },
];

function getSeenVersions(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch {
    return new Set();
  }
}

function markSeen(versions: string[]) {
  const seen = getSeenVersions();
  versions.forEach((v) => seen.add(v));
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...seen]));
}

const WhatsNewDialog = () => {
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [unseenFeatures, setUnseenFeatures] = useState<Feature[]>([]);
  const [imgErrors, setImgErrors] = useState<Set<number>>(new Set());

  useEffect(() => {
    const DEMO_MODE = false;

    const seen = DEMO_MODE ? new Set<string>() : getSeenVersions();
    const unseen = allFeatures.filter((f) => !seen.has(f.version));
    if (unseen.length > 0) {
      setUnseenFeatures(unseen);
      setOpen(true);
    }
  }, []);

  const handleClose = () => {
    markSeen(unseenFeatures.map((f) => f.version));
    setOpen(false);
  };

  const handleNext = () => {
    if (activeStep < unseenFeatures.length - 1) {
      setActiveStep(activeStep + 1);
    } else {
      handleClose();
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const handleImgError = (index: number) => {
    setImgErrors((prev) => new Set(prev).add(index));
  };

  if (unseenFeatures.length === 0) return null;

  const current = unseenFeatures[activeStep];
  const showImage = current.image && !imgErrors.has(activeStep);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          maxHeight: "90vh",
        },
      }}
    >
      {/* Hero area — screenshot or icon fallback */}
      <Box sx={{ position: "relative" }}>
        {showImage ? (
          <Box
            sx={{
              width: "100%",
              height: 280,
              bgcolor: "#f5f5f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={current.image}
              alt={current.label}
              onError={() => handleImgError(activeStep)}
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: 200,
              background: `linear-gradient(135deg, ${current.color || "#1a73e8"}, ${current.color || "#1a73e8"}dd)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {current.icon}
          </Box>
        )}
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="close"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            bgcolor: "rgba(0,0,0,0.4)",
            color: "#fff",
            "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        {/* Step counter chip */}
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 12,
            bgcolor: "rgba(0,0,0,0.4)",
            color: "#fff",
            borderRadius: 2,
            px: 1.5,
            py: 0.25,
            fontSize: "0.75rem",
            fontWeight: 500,
          }}
        >
          {activeStep + 1} / {unseenFeatures.length}
        </Box>
      </Box>

      {/* Content */}
      <DialogContent sx={{ pt: 3, pb: 1, px: 3 }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {current.label}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          {current.description}
        </Typography>
      </DialogContent>

      {/* Navigation */}
      <DialogActions sx={{ px: 2, pb: 2, pt: 0, flexDirection: "column", gap: 1 }}>
        <MobileStepper
          variant="dots"
          steps={unseenFeatures.length}
          position="static"
          activeStep={activeStep}
          sx={{
            width: "100%",
            bgcolor: "transparent",
            justifyContent: "space-between",
            "& .MuiMobileStepper-dot": { mx: 0.5 },
          }}
          nextButton={
            <Button
              size="small"
              onClick={handleNext}
              variant={activeStep === unseenFeatures.length - 1 ? "contained" : "text"}
              color={activeStep === unseenFeatures.length - 1 ? "success" : "primary"}
            >
              {activeStep === unseenFeatures.length - 1 ? "Get Started" : "Next"}
              {activeStep < unseenFeatures.length - 1 && <KeyboardArrowRight />}
            </Button>
          }
          backButton={
            <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
              <KeyboardArrowLeft />
              Back
            </Button>
          }
        />
        <Button onClick={handleClose} color="inherit" size="small" sx={{ opacity: 0.6 }}>
          Skip tour
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsNewDialog;
